import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  CalendarDays,
  Crown,
  LayoutDashboard,
  MapPin,
  MoreHorizontal,
  Package,
  Plus,
  Sparkles,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import HiveFlowLogo from "@/components/HiveFlowLogo";
import { useAuth } from "@/context/AuthContext";
import { useNativeOffline } from "@/context/NativeOfflineContext";
import { api } from "@/lib/api";
import { sessionCanWrite } from "@/lib/permissions";
import { toast } from "sonner";
import useKeyboardOpen from "@/hooks/useKeyboardOpen";

const NAV_ITEMS = [
  { to: "/", label: "Overzicht", testKey: "vandaag", icon: LayoutDashboard, end: true },
  { to: "/planning", label: "Werk", icon: CalendarDays, workBadge: true },
  { to: "/kasten", label: "Kasten", icon: Package },
  { to: "/bijenstanden", label: "Standen", icon: MapPin },
  { to: "/meer", label: "Meer", icon: MoreHorizontal },
];

const DESKTOP_ITEMS = [
  { to: "/", label: "Overzicht", testKey: "vandaag", icon: LayoutDashboard, end: true },
  { to: "/planning", label: "Werk", icon: CalendarDays, workBadge: true },
  { to: "/bijenstanden", label: "Bijenstanden", icon: MapPin },
  { to: "/kasten", label: "Kasten", icon: Package },
  { to: "/koninginnen", label: "Koninginnen", icon: Crown, feature: "queenBreeding" },
  { to: "/teelt", label: "Koninginnenteelt", icon: Sparkles, feature: "queenBreeding" },
];

const PAGE_ACTIONS = [
  { match: /^\/planning$/, to: "/planning?nieuw=1", label: "Taak toevoegen" },
  { match: /^\/kasten$/, to: "/kasten?nieuw=1", label: "Kast toevoegen" },
  { match: /^\/bijenstanden$/, to: "/bijenstanden?nieuw=1", label: "Stand toevoegen" },
  { match: /^\/koninginnen$/, to: "/koninginnen?nieuw=1", label: "Koningin toevoegen" },
  { match: /^\/teelt$/, to: "/teelt?nieuw=1", label: "Teelt starten" },
];

function navTestKey(item) {
  return item.testKey || item.label.toLowerCase();
}

export default function AppShell() {
  const { user, features, logout, verifyEmail } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const native = useNativeOffline();
  const [badges, setBadges] = useState({});
  const [emailCode, setEmailCode] = useState("");
  const [showEmailCode, setShowEmailCode] = useState(false);
  const [emailBusy, setEmailBusy] = useState(false);
  useKeyboardOpen();

  useEffect(() => {
    let cancelled = false;
    const fetchBadges = async () => {
      try {
        const response = await api.get("/dashboard");
        if (!cancelled) setBadges(response.data?.stats || {});
      } catch {}
    };
    fetchBadges();
    const timer = setInterval(fetchBadges, 60000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [location.pathname]);

  useEffect(() => {
    const onQueued = (event) => toast.success(event.detail?.message || "Opgeslagen op deze telefoon");
    window.addEventListener("hiveflow:offline-command-queued", onQueued);
    return () => window.removeEventListener("hiveflow:offline-command-queued", onQueued);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get("invite");
    if (token && location.pathname !== "/team") {
      navigate(`/team?invite=${encodeURIComponent(token)}`, { replace: true });
    }
  }, [location.pathname, location.search, navigate]);

  const nativeConflicts = native.nativeApp
    ? Math.max(native.status.conflicts || 0, native.conflicts.length)
    : 0;

  const pageAction = useMemo(() => {
    if (!sessionCanWrite(user)) return null;
    return PAGE_ACTIONS.find((action) => action.match.test(location.pathname)) || null;
  }, [location.pathname, user]);

  const submitEmailCode = async (event) => {
    event.preventDefault();
    if (!emailCode || emailCode.length < 6) return;
    setEmailBusy(true);
    try {
      await verifyEmail({ email: user.email, code: emailCode });
      setEmailCode("");
      toast.success("E-mailadres bevestigd");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Bevestigen mislukt");
    } finally {
      setEmailBusy(false);
    }
  };

  const resendEmailCode = async () => {
    setEmailBusy(true);
    try {
      await api.post("/auth/resend-verification", { email: user.email });
      toast.success("Nieuwe code verstuurd");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Code versturen mislukt");
    } finally {
      setEmailBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f7f6f2]">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-60 flex-col border-r border-stone-200 bg-[#fffefb] md:flex">
        <div className="flex h-16 items-center gap-3 px-5">
          <HiveFlowLogo className="h-9 w-9" />
          <div className="font-display text-lg font-bold tracking-tight text-zinc-950">bijen.app</div>
        </div>

        <nav className="flex-1 px-3 py-3">
          {DESKTOP_ITEMS.filter((item) => !item.feature || features?.[item.feature]).map((item) => {
            const badge = item.workBadge ? (badges.open_tasks || 0) + (badges.open_suggestions || 0) : 0;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={`nav-desktop-${navTestKey(item)}`}
                className={({ isActive }) =>
                  `mb-0.5 flex min-h-11 items-center gap-3 rounded-lg px-3 text-sm font-semibold transition-colors ${
                    isActive ? "bg-amber-50 text-amber-900" : "text-zinc-600 hover:bg-stone-50 hover:text-zinc-950"
                  }`
                }
              >
                <item.icon className="h-5 w-5" strokeWidth={2.25} />
                <span className="flex-1">{item.label}</span>
                {badge > 0 && (
                  <span className="min-w-5 rounded-full bg-zinc-900 px-1.5 py-0.5 text-center text-[10px] font-bold text-white">
                    {badge > 99 ? "99+" : badge}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>

        <div className="border-t border-stone-100 px-5 py-4">
          <div className="truncate text-sm font-semibold text-zinc-800">{user?.name}</div>
          <div className="truncate text-xs text-zinc-500">{user?.email}</div>
          <button
            type="button"
            onClick={logout}
            data-testid="logout-btn-desktop"
            className="mt-2 min-h-9 text-xs font-semibold text-zinc-500 hover:text-zinc-900"
          >
            Uitloggen
          </button>
        </div>
      </aside>

      <main
        className="min-h-screen pt-safe md:ml-60"
        style={{ paddingBottom: "calc(5.1rem + env(safe-area-inset-bottom, 0px))" }}
      >
        <div className="mx-auto max-w-6xl px-4 py-3 md:px-8 md:py-6">
          {user?.email_verified === false && (
            <form
              onSubmit={submitEmailCode}
              data-testid="email-verification-banner"
              className="mb-4 border-l-2 border-amber-500 bg-amber-50 px-3 py-3"
            >
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-bold text-amber-950">Bevestig je e-mailadres</div>
                  <div className="text-xs leading-5 text-amber-900/75">Nodig voor account-herstel en teamfuncties.</div>
                </div>
                <div className="flex items-center gap-2">
                  <button type="button" onClick={resendEmailCode} disabled={emailBusy} className="min-h-10 text-xs font-bold text-amber-900">Mail opnieuw</button>
                  <button type="button" onClick={() => setShowEmailCode((value) => !value)} className="min-h-10 text-xs font-bold text-amber-900">Code invullen</button>
                </div>
              </div>
              {showEmailCode && (
                <div className="mt-2 flex items-center gap-2">
                  <Input
                    value={emailCode}
                    onChange={(event) => setEmailCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    data-testid="email-verification-code"
                    placeholder="000000"
                    className="h-10 w-36 bg-white tracking-[0.2em] font-bold"
                  />
                  <Button disabled={emailBusy || emailCode.length < 6} className="h-10 bg-zinc-900 text-xs font-bold hover:bg-zinc-800">Bevestig</Button>
                </div>
              )}
            </form>
          )}

          {nativeConflicts > 0 && (
            <div
              data-testid="sync-banner"
              className="mb-4 flex min-h-11 items-center gap-3 border-l-2 border-red-500 bg-red-50 px-3 text-sm font-semibold text-red-900"
            >
              <span className="h-2.5 w-2.5 rounded-full bg-red-500" />
              <span className="flex-1">{nativeConflicts} {nativeConflicts === 1 ? "wijziging vraagt" : "wijzigingen vragen"} aandacht</span>
              <button type="button" onClick={() => navigate("/meer")} className="min-h-10 font-bold underline underline-offset-2" data-testid="sync-conflicts">Bekijken</button>
            </div>
          )}

          <Outlet />
        </div>
      </main>

      {pageAction && (
        <button
          type="button"
          onClick={() => navigate(pageAction.to)}
          data-testid="fab-quick-add"
          aria-label={pageAction.label}
          className="fab-hide-on-keyboard fixed bottom-[5.15rem] right-4 z-40 inline-flex min-h-12 items-center gap-2 rounded-xl bg-[#cf7008] px-4 text-sm font-bold text-white shadow-md shadow-zinc-900/10 btn-press hover:bg-[#bc6508] md:bottom-6"
          style={{ marginBottom: "env(safe-area-inset-bottom, 0px)" }}
        >
          <Plus className="h-4 w-4" strokeWidth={2.7} />
          {pageAction.label}
        </button>
      )}

      <nav
        className="fixed inset-x-0 bottom-0 z-30 border-t border-stone-200 bg-[#fffefb] md:hidden"
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      >
        <div className="grid grid-cols-5">
          {NAV_ITEMS.map((item) => {
            const badge = item.workBadge ? (badges.open_tasks || 0) + (badges.open_suggestions || 0) : 0;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={`nav-mobile-${navTestKey(item)}`}
                className={({ isActive }) =>
                  `relative flex min-h-[63px] flex-col items-center justify-center gap-1 px-1 pt-1 text-[11px] font-semibold transition-colors ${
                    isActive ? "text-[#b95f05]" : "text-zinc-500"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {isActive && <span className="absolute left-1/2 top-0 h-0.5 w-7 -translate-x-1/2 rounded-full bg-[#cf7008]" />}
                    <span className="relative">
                      <item.icon className="h-5 w-5" strokeWidth={isActive ? 2.6 : 2.1} />
                      {badge > 0 && (
                        <span
                          data-testid={`badge-mobile-${navTestKey(item)}`}
                          className="absolute -right-3 -top-2 min-w-[17px] rounded-full bg-zinc-900 px-1 py-0.5 text-center text-[9px] font-bold leading-3 text-white"
                        >
                          {badge > 99 ? "99+" : badge}
                        </span>
                      )}
                    </span>
                    <span>{item.label}</span>
                  </>
                )}
              </NavLink>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
